import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import CommonFormLayout from "../../OptmisedCode/Form/CommonFormLayout";
import { MAIN_URL } from "../../../Configurations/Urls";

const BASE_ROUTE = "/organization/client-payments";

const EMPTY_FORM = {
  client_id: "",
  payment_date: "",
  payment_reference_number: "",
  currency: "INR",
  payment_amount: "",
  payment_description: "",
  payment_method: "",
  payment_status: "paid",
  notes: "",
};

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toDateInput(value) {
  if (!value || value === "-") return "";
  const s = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
}

export default function ClientPaymentForm({ mode = "add" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = mode === "edit";
  const isView = mode === "view";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const loadOptions = useCallback(async () => {
    try {
      const res = await axios.get(
        `${MAIN_URL}/api/client-management/clients/data`,
        { headers: authHeaders() }
      );
      setClients(res.data?.data || []);
    } catch (err) {
      toast.error(
        err.response?.data?.message || err.message || "Failed to load clients."
      );
      setClients([]);
    }
  }, []);

  const loadPayment = useCallback(async () => {
    if ((!isEdit && !isView) || !id) return;
    const res = await axios.get(
      `${MAIN_URL}/api/client-management/client-payments/${id}`,
      { headers: authHeaders() }
    );
    const data = res.data?.data || res.data || {};
    setForm({
      client_id: data.client_id ? String(data.client_id) : "",
      payment_date: toDateInput(data.payment_date),
      payment_reference_number: data.payment_reference_number || "",
      currency: data.currency || "INR",
      payment_amount:
        data.payment_amount != null && data.payment_amount !== "-"
          ? String(data.payment_amount)
          : "",
      payment_description: data.payment_description || "",
      payment_method: data.payment_method || "",
      payment_status: data.payment_status || "paid",
      notes: data.notes || "",
    });
  }, [id, isEdit, isView]);

  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      setLoading(true);
      try {
        await loadOptions();
        if (!cancelled) await loadPayment();
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err.response?.data?.error ||
              err.response?.data?.message ||
              err.message ||
              "Failed to load payment."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [loadOptions, loadPayment]);

  const validate = () => {
    const next = {};
    if (!form.client_id) next.client_id = "Client is required.";
    if (form.payment_amount && Number.isNaN(Number(form.payment_amount))) {
      next.payment_amount = "Enter a valid amount.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_id: Number(form.client_id),
        payment_amount: form.payment_amount === "" ? 0 : Number(form.payment_amount),
        payment_date: form.payment_date || null,
      };
      if (isEdit) {
        await axios.put(
          `${MAIN_URL}/api/client-management/client-payments/${id}`,
          payload,
          { headers: authHeaders() }
        );
        toast.success("Payment updated.");
      } else {
        await axios.post(
          `${MAIN_URL}/api/client-management/client-payments`,
          payload,
          { headers: authHeaders() }
        );
        toast.success("Payment added.");
      }
      navigate(BASE_ROUTE);
    } catch (err) {
      const apiErrors = err.response?.data?.errors || {};
      const mapped = {};
      Object.entries(apiErrors).forEach(([key, value]) => {
        mapped[key] = Array.isArray(value) ? value[0] : String(value);
      });
      if (Object.keys(mapped).length) setErrors(mapped);
      toast.error(
        mapped.client_id ||
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Save failed."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <CommonFormLayout
      mode={mode}
      loading={loading}
      btnLoading={saving}
      onSubmit={handleSave}
      wrapInGrid={false}
      headerProps={{
        addMessage: "Add client payment",
        updateMessage: "Edit client payment",
        homeLink: BASE_ROUTE,
        homeText: "Client payments",
        editLink: id ? `${BASE_ROUTE}/edit/${id}` : undefined,
      }}
    >
      <div className="ems-fm-grid">
        <div className="ems-fm-field">
          <label>
            Client {!isView && <span className="ems-fm-req">*</span>}
          </label>
          <select
            className="ems-fm-input"
            value={form.client_id}
            disabled={isView}
            onChange={(e) => setField("client_id", e.target.value)}
          >
            <option value="">Select client</option>
            {clients.map((client) => (
              <option key={client.client_id} value={String(client.client_id)}>
                {client.client_name}
              </option>
            ))}
          </select>
          {errors.client_id ? (
            <span className="ems-fm-err">{errors.client_id}</span>
          ) : null}
        </div>

        <div className="ems-fm-field">
          <label>Payment date</label>
          <input
            type="date"
            className="ems-fm-input"
            value={form.payment_date}
            readOnly={isView}
            onChange={(e) => setField("payment_date", e.target.value)}
          />
        </div>

        <div className="ems-fm-field">
          <label>Reference number</label>
          <input
            className="ems-fm-input"
            value={form.payment_reference_number}
            readOnly={isView}
            onChange={(e) =>
              setField("payment_reference_number", e.target.value)
            }
            placeholder="Invoice / txn reference"
          />
        </div>

        <div className="ems-fm-field">
          <label>Amount</label>
          <div className="ems-fm-money">
            <span className="ems-fm-money-pre">₹</span>
            <input
              className="ems-fm-input"
              value={form.payment_amount}
              readOnly={isView}
              onChange={(e) => setField("payment_amount", e.target.value)}
              placeholder="0.00"
            />
          </div>
          {errors.payment_amount ? (
            <span className="ems-fm-err">{errors.payment_amount}</span>
          ) : null}
        </div>

        <div className="ems-fm-field">
          <label>Currency</label>
          <input
            className="ems-fm-input"
            value={form.currency}
            readOnly={isView}
            onChange={(e) => setField("currency", e.target.value.toUpperCase())}
          />
        </div>

        <div className="ems-fm-field">
          <label>Payment method</label>
          <input
            className="ems-fm-input"
            value={form.payment_method}
            readOnly={isView}
            onChange={(e) => setField("payment_method", e.target.value)}
            placeholder="UPI, bank transfer, card…"
          />
        </div>

        <div className="ems-fm-field">
          <label>Status</label>
          <select
            className="ems-fm-input"
            value={form.payment_status}
            disabled={isView}
            onChange={(e) => setField("payment_status", e.target.value)}
          >
            <option value="paid">Paid</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="refunded">Refunded</option>
          </select>
        </div>

        <div className="ems-fm-field full">
          <label>Description</label>
          <input
            className="ems-fm-input"
            value={form.payment_description}
            readOnly={isView}
            onChange={(e) => setField("payment_description", e.target.value)}
            placeholder="What this payment is for"
          />
        </div>

        <div className="ems-fm-field full">
          <label>Notes</label>
          <textarea
            className="ems-fm-input ems-fm-textarea"
            value={form.notes}
            readOnly={isView}
            onChange={(e) => setField("notes", e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </CommonFormLayout>
  );
}
